'use client';

import { CalendarDays, Play } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';
import Slider from 'react-slick';

import { SpecularBentoCard } from '@/components/flagship/dom/SpecularBentoCard';
import { SplitReveal } from '@/components/flagship/dom/SplitReveal';
import { FlagshipProse } from '@/components/flagship/page/FlagshipProse';
import { ReadingProgress } from '@/components/flagship/page/ReadingProgress';
import VideoPopup from '@/components/modals/VideoPopup';
import { ArticleType } from '@/lib/api/article-api';

import ArticleRelatedPost from '../article-common/article-related-post';
import ArticleSidebar from '../article-common/article-sidebar';

const SLIDER_SETTINGS = {
  dots: true,
  infinite: true,
  arrows: true,
  speed: 900,
  slidesToShow: 1,
  slidesToScroll: 1,
  fade: true,
  // The previous `prevArrow`/`nextArrow` overrides passed `onClick: () => {}`,
  // which react-slick then overwrote — they were dead props dressed up as
  // configuration. Default arrows are styled in CSS instead.
};

const ArticleDetailsArea: React.FC<{ article: ArticleType; articles: ArticleType[] }> = ({
  article,
  articles,
}) => {
  const t = useTranslations('Article');
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  const [videoId, setVideoId] = useState('');

  const hasSlider = Boolean(article.slider && article.slider.length > 0);

  return (
    <div className="flagship relative">
      <ReadingProgress target="[data-article-body]" />

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 pb-24 pt-[120px] md:pt-[140px] lg:grid-cols-[minmax(0,1fr)_320px]">
        <article data-article-body className="min-w-0">
          {/* --- Title block ------------------------------------------------ */}
          <header>
            {article.tags?.length ? (
              <div className="mb-5 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="fg-ink-muted rounded-full border border-[var(--fg-line)] px-3 py-1 font-inter text-[10px] uppercase tracking-[0.18em]"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            <SplitReveal
              as="h1"
              text={article.title}
              splitBy="words"
              stagger={0.04}
              className="fg-display fg-ink text-[clamp(1.9rem,4.4vw,3.1rem)]"
            />

            <p className="fg-ink-muted mt-5 flex items-center gap-2 font-inter text-sm">
              <CalendarDays className="h-4 w-4" />
              {article.date}
            </p>
          </header>

          {/* --- Media ------------------------------------------------------ */}
          {hasSlider || article.img ? (
            <div className="relative mt-9">
              <SpecularBentoCard contentClassName="relative overflow-hidden">
                {hasSlider ? (
                  <Slider {...SLIDER_SETTINGS}>
                    {article.slider!.map((slide, index) => (
                      <div key={index} className="relative h-[280px] md:h-[440px]">
                        <Image
                          src={slide}
                          alt={`${article.title} — ${index + 1}`}
                          fill
                          sizes="(max-width: 1024px) 100vw, 760px"
                          className="object-cover"
                        />
                      </div>
                    ))}
                  </Slider>
                ) : (
                  <div className="relative h-[280px] md:h-[440px]">
                    <Image
                      src={article.img}
                      alt={article.title}
                      fill
                      priority
                      sizes="(max-width: 1024px) 100vw, 760px"
                      className="object-cover"
                    />
                  </div>
                )}
              </SpecularBentoCard>

              {article.videoId ? (
                <button
                  type="button"
                  onClick={() => {
                    setVideoId(article.videoId!);
                    setIsVideoOpen(true);
                  }}
                  aria-label={t('playVideo')}
                  className="fg-glass-media absolute left-1/2 top-1/2 z-[4] flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full text-white transition-transform duration-300 hover:scale-110 active:scale-95"
                >
                  <Play className="h-6 w-6 translate-x-[2px] fill-current" />
                </button>
              ) : null}
            </div>
          ) : null}

          {/* --- Body ------------------------------------------------------- */}
          <div className="mt-10 max-w-[68ch]">
            <FlagshipProse content={article.content} />
          </div>

          <ArticleRelatedPost article={article} articles={articles} />
        </article>

        <ArticleSidebar article={article} articles={articles} />
      </div>

      <VideoPopup isVideoOpen={isVideoOpen} setIsVideoOpen={setIsVideoOpen} videoId={videoId} />
    </div>
  );
};

export default ArticleDetailsArea;
